import React from 'react';
import { useParams } from 'react-router-dom';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { LoadingSpinner } from '../components/LoadingSpinner';

import { useGroupDetails } from '../hooks/useGroupDetails';
import { GroupHeader } from './group-details/GroupHeader';
import { GroupSummaryCards } from './group-details/GroupSummaryCards';
import { GroupTabs } from './group-details/GroupTabs';
import { GroupExpensesList } from './group-details/GroupExpensesList';
import { GroupTasksList } from './group-details/GroupTasksList';
import { GroupMembersList } from './group-details/GroupMembersList';
import { AddExpenseModal } from './group-details/modals/AddExpenseModal';

export const GroupDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();

  const {
    // Data
    group,
    expenses,
    tasks,
    memberProfiles,
    loading,
    currUser,
    activeTab, setActiveTab,

    // Form States
    isAddModalOpen, setIsAddModalOpen,
    editingExpense,
    description, setDescription,
    amount, setAmount,
    expenseCurrency, setExpenseCurrency,
    expenseDate, setExpenseDate,
    selectedMemberIds,
    splitMode, setSplitMode,
    exactAmounts, setExactAmounts,
    percentages, setPercentages,

    // Task Form States
    newTaskTitle, setNewTaskTitle,

    // Dialog States
    isDeleteExpenseDialogOpen, setIsDeleteExpenseDialogOpen,

    // Actions
    handleAddExpense,
    handleEditClick,
    handleDeleteExpenseConfirm,
    resetExpenseForm,
    toggleMemberSelection,
    handleAddTask,
    handleToggleTask,
    handleDeleteTask,
  } = useGroupDetails(id);

  if (!group && !loading) return <div className="py-12 text-center text-text-muted">Group not found</div>;

  return (
    <div className="space-y-6">
      <GroupHeader group={group} />

      {loading || !group ? (
        <div className="flex justify-center py-12">
          <LoadingSpinner size="lg" />
        </div>
      ) : (
        <>
          <GroupSummaryCards
        expenses={expenses}
        group={group}
        currentUser={currUser}
      />

      <GroupTabs
        activeTab={activeTab}
        onTabChange={setActiveTab}
      />

      {activeTab === 'expenses' && (
        <GroupExpensesList
          expenses={expenses}
          memberProfiles={memberProfiles}
          currentUser={currUser}
          onEditExpense={handleEditClick}
          onResetForm={resetExpenseForm}
          onOpenAddModal={() => { setIsAddModalOpen(true); }}
        />
      )}

      {activeTab === 'tasks' && (
        <GroupTasksList
          tasks={tasks}
          newTaskTitle={newTaskTitle}
          onNewTaskTitleChange={setNewTaskTitle}
          onAddTask={handleAddTask}
          onToggleTask={handleToggleTask}
          onDeleteTask={handleDeleteTask}
        />
      )}

      {activeTab === 'members' && (
        <GroupMembersList
          memberProfiles={memberProfiles}
          group={group}
          currentUser={currUser}
        />
      )}
        </>
      )}

      <AddExpenseModal
        isOpen={isAddModalOpen}
        onClose={() => {
          setIsAddModalOpen(false);
          resetExpenseForm();
        }}
        isEditing={!!editingExpense}

        description={description} setDescription={setDescription}
        amount={amount} setAmount={setAmount}
        currency={expenseCurrency} setCurrency={setExpenseCurrency}
        date={expenseDate} setDate={setExpenseDate}

        members={memberProfiles}
        selectedMemberIds={selectedMemberIds}
        toggleMemberSelection={toggleMemberSelection}

        splitMode={splitMode} setSplitMode={setSplitMode}
        exactAmounts={exactAmounts} setExactAmounts={setExactAmounts}
        percentages={percentages} setPercentages={setPercentages}

        onSubmit={handleAddExpense}
        {...(editingExpense ? { onDelete: () => { setIsDeleteExpenseDialogOpen(true); } } : {})}
      />

      <ConfirmDialog
        isOpen={isDeleteExpenseDialogOpen}
        onClose={() => { setIsDeleteExpenseDialogOpen(false); }}
        onConfirm={handleDeleteExpenseConfirm}
        title="Delete Expense"
        message="Are you sure you want to delete this expense?"
        confirmText="Delete"
        variant="danger"
      />
    </div>
  );
};
